import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Divider,
  Space,
  message,
} from "antd";
import { SaveOutlined, SendOutlined, FileOutlined } from "@ant-design/icons";
import { handleApiError } from "utils";
import {
  billingInvoiceByIdService,
  billingInvoiceCreateService,
  billingInvoiceUpdateService,
  billingInvoiceNextNumberService,
  billingItemListService,
  billingCustomerListService,
  billingCustomerDetailsService,
  billingSalesAgentListService,
  billingSettingsGetService,
  billingTaxRateResolveService,
} from "services/billing";
import LineItems from "./_lineItems";
import { computeInvoiceTotals, money } from "../constants";
import { handleInvoiceSaveResponse } from "./_emailFeedback";

const { Option } = Select;

export default function InvoiceForm({ id }) {
  const history = useHistory();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [billToState, setBillToState] = useState("");
  // Tax rates for the customer's state by product type, or null when no state
  // is set. New and changed lines take these, not just the lines that existed
  // when the customer was picked.
  const [stateRates, setStateRates] = useState(null);

  // watched fields for live totals
  const discountType = Form.useWatch("discount_type", form);
  const discountValue = Form.useWatch("discount_value", form);
  const adjustment = Form.useWatch("adjustment", form);

  const totals = computeInvoiceTotals(items, discountType, discountValue, adjustment);

  useEffect(() => {
    // Load reference data
    Promise.all([
      billingItemListService(),
      billingCustomerListService(),
      billingSalesAgentListService(),
    ])
      .then(([itemsRes, custRes, agentsRes]) => {
        setCatalog(itemsRes.data.items || []);
        setCustomers(custRes.data.items || []);
        setAgents(agentsRes.data.items || []);
      })
      .catch((error) => handleApiError(error));

    if (id) {
      loadInvoice();
    } else {
      initNewInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const initNewInvoice = () => {
    billingSettingsGetService()
      .then((response) => {
        const s = response.data.item || {};
        setCurrency(s.default_currency || "USD");
        form.setFieldsValue({
          status: "DRAFT",
          discount_type: "FIXED",
          discount_value: 0,
          adjustment: 0,
          currency: s.default_currency || "USD",
          terms_conditions: s.default_terms_conditions || "",
          client_note: s.default_client_note || "",
          invoice_date: moment(),
          invoice_due_date: moment().add(s.default_due_days || 15, "days"),
          service_month: moment(),
        });
      })
      .catch(() => {});
    billingInvoiceNextNumberService()
      .then((response) => {
        form.setFieldsValue({
          invoice_number: response.data.next_invoice_number,
        });
      })
      .catch(() => {});
  };

  const loadInvoice = () => {
    setIsLoading(true);
    billingInvoiceByIdService(id)
      .then((response) => {
        setIsLoading(false);
        const inv = response.data.item;
        if (!inv) return;
        setCurrency(inv.currency || "USD");
        setBillToState(inv.bill_to_state || "");
        loadStateRates(inv.bill_to_state).catch(() => {});
        setItems(
          (inv.items || []).map((l, i) => ({ ...l, key: l._id || i }))
        );
        form.setFieldsValue({
          ...inv,
          company: inv.company && inv.company._id ? inv.company._id : inv.company,
          customer: inv.customer && inv.customer._id ? inv.customer._id : inv.customer,
          sales_agent:
            inv.sales_agent && inv.sales_agent._id
              ? inv.sales_agent._id
              : inv.sales_agent,
          invoice_date: inv.invoice_date ? moment(inv.invoice_date) : null,
          invoice_due_date: inv.invoice_due_date ? moment(inv.invoice_due_date) : null,
          service_month: inv.service_month ? moment(inv.service_month) : null,
          tags: inv.tags || [],
        });
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  // When a customer is selected, pre-fill billing details + state, then
  // auto-apply state tax to the taxable lines.
  const onCustomerChange = useCallback(
    (companyId) => {
      if (!companyId) return;
      billingCustomerDetailsService(companyId)
        .then((response) => {
          const d = response.data.item || {};
          setBillToState(d.bill_to_state || "");
          form.setFieldsValue({
            bill_to_name: d.bill_to_name,
            bill_to_email: d.bill_to_email,
            bill_to_address: d.bill_to_address,
            bill_to_state: d.bill_to_state,
          });
          applyStateTax(d.bill_to_state);
        })
        .catch((error) => handleApiError(error));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const loadStateRates = (state) => {
    if (!state) {
      setStateRates(null);
      return Promise.resolve(null);
    }
    return Promise.all([
      billingTaxRateResolveService(state, "SERVICE"),
      billingTaxRateResolveService(state, "PHYSICAL_GOOD"),
    ]).then(([svc, goods]) => {
      const rates = {
        SERVICE: svc.data.rate_percentage || 0,
        PHYSICAL_GOOD: goods.data.rate_percentage || 0,
      };
      setStateRates(rates);
      return rates;
    });
  };

  const applyStateTax = (state) => {
    loadStateRates(state)
      .then((rates) => {
        if (!rates) return;
        setItems((prev) =>
          prev.map((l) =>
            l.is_taxable === false
              ? l
              : { ...l, tax_percentage: rates[l.product_type] || 0 }
          )
        );
        message.success("Applied state tax rates for " + state);
      })
      .catch(() => {});
  };

  const buildPayload = (values, status) => {
    return {
      ...values,
      status,
      invoice_date: values.invoice_date
        ? values.invoice_date.format("YYYY-MM-DD")
        : null,
      invoice_due_date: values.invoice_due_date
        ? values.invoice_due_date.format("YYYY-MM-DD")
        : null,
      service_month: values.service_month
        ? values.service_month.startOf("month").format("YYYY-MM-DD")
        : null,
      service_period_label: values.service_month
        ? values.service_month.format("MMMM YYYY")
        : "",
      items: items.map((l) => ({
        product_id: l.product_id || null,
        product_type: l.product_type,
        product_name: l.product_name,
        description: l.description,
        unit: l.unit,
        quantity: Number(l.quantity) || 0,
        rate: Number(l.rate) || 0,
        discount_percentage: Number(l.discount_percentage) || 0,
        is_taxable: l.is_taxable !== false,
        tax_rate_id: l.tax_rate_id || null,
        tax_percentage: l.is_taxable === false ? 0 : Number(l.tax_percentage) || 0,
      })),
    };
  };

  const submit = (status) => {
    form
      .validateFields()
      .then((values) => {
        if (items.length === 0) {
          message.error("Add at least one line item");
          return;
        }
        if (status === "SENT") {
          const email = (values.bill_to_email || "").trim();
          if (!email) {
            message.error("Bill To email is required to send the invoice.");
            return;
          }
        }
        setIsLoading(true);
        const payload = buildPayload(values, status);
        const action = id
          ? billingInvoiceUpdateService(id, payload)
          : billingInvoiceCreateService(payload);
        action
          .then((response) => {
            setIsLoading(false);
            handleInvoiceSaveResponse(response);
            history.push("/billing/invoices");
          })
          .catch((error) => {
            setIsLoading(false);
            handleApiError(error);
          });
      })
      .catch(() => message.error("Please complete the required fields"));
  };

  return (
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Customer & Invoice Details" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Customer"
                  name="company"
                  rules={[{ required: true, message: "Select a customer" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select customer"
                    optionFilterProp="children"
                    onChange={onCustomerChange}
                  >
                    {customers.map((c) => (
                      <Option key={c._id} value={c._id}>
                        {c.company_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Invoice Number" name="invoice_number">
                  <Input placeholder="INV-000001" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Bill To (Name)" name="bill_to_name">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Bill To (Email)" name="bill_to_email">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item label="Bill To (Address)" name="bill_to_address">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="State" name="bill_to_state" help="Drives auto tax">
                  <Input
                    onBlur={(e) => {
                      setBillToState(e.target.value);
                      applyStateTax(e.target.value);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Line Items" size="small" style={{ marginBottom: 16 }}>
            <LineItems
              items={items}
              onChange={setItems}
              catalog={catalog}
              currency={currency}
              stateRates={stateRates}
            />
          </Card>

          <Card title="Notes & Terms" size="small">
            <Form.Item
              label="Client Note (visible to customer)"
              name="client_note"
            >
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item label="Admin Note (private)" name="admin_note">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item label="Terms & Conditions" name="terms_conditions">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Settings" size="small" style={{ marginBottom: 16 }}>
            <Form.Item label="Sales Agent" name="sales_agent">
              <Select showSearch allowClear optionFilterProp="children" placeholder="Select agent">
                {agents.map((a) => (
                  <Option key={a._id} value={a._id}>
                    {a.display_name ||
                      `${a.first_name || ""} ${a.last_name || ""}`.trim() ||
                      a.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Invoice Date" name="invoice_date">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Due Date" name="invoice_due_date">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Service Month"
              name="service_month"
              help="The month the services/invoice is for"
            >
              <DatePicker picker="month" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Currency" name="currency">
              <Input />
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <Select mode="tags" placeholder="Type and press Enter" />
            </Form.Item>
            <Form.Item
              name="prevent_overdue_reminders"
              valuePropName="checked"
            >
              <Checkbox>Do not send overdue reminders for this invoice</Checkbox>
            </Form.Item>
            <Form.Item name="is_recurring" valuePropName="checked">
              <Checkbox>Mark as recurring (manage in Recurring section)</Checkbox>
            </Form.Item>
          </Card>

          <Card title="Totals" size="small">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Discount Type" name="discount_type">
                  <Select>
                    <Option value="FIXED">Fixed $</Option>
                    <Option value="PERCENT">Percent %</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Discount" name="discount_value">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Adjustment (+/-)" name="adjustment">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Divider style={{ margin: "8px 0" }} />
            <SummaryRow label="Subtotal" value={money(totals.subtotal, currency)} />
            <SummaryRow label="Discount" value={"-" + money(totals.discount_amount, currency)} />
            <SummaryRow label="Tax" value={money(totals.tax_total, currency)} />
            <SummaryRow label="Adjustment" value={money(adjustment || 0, currency)} />
            <Divider style={{ margin: "8px 0" }} />
            <SummaryRow
              label={<strong>Grand Total</strong>}
              value={<strong>{money(totals.grand_total, currency)}</strong>}
            />
          </Card>

          <Card size="small" style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                block
                icon={<FileOutlined />}
                loading={isLoading}
                onClick={() => submit("DRAFT")}
              >
                Save as Draft
              </Button>
              <Button
                block
                type="default"
                icon={<SaveOutlined />}
                loading={isLoading}
                onClick={() => submit("SAVED")}
              >
                Save (Live, no email)
              </Button>
              <Button
                block
                type="primary"
                icon={<SendOutlined />}
                loading={isLoading}
                onClick={() => submit("SENT")}
              >
                Save &amp; Send
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </Form>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
